import { DollarSign, TrendingUp, Package, CalendarClock, Trophy } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { CommercialSummary } from "@/hooks/useLeadCommercialProfile";

function formatCurrency(v: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);
}

interface LeadCommercialSectionProps {
  summary: CommercialSummary | null;
}

export function LeadCommercialSection({ summary }: LeadCommercialSectionProps) {
  if (!summary) return null;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        {/* Open deals */}
        <div className="rounded-lg border bg-muted/30 p-3 space-y-1">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <DollarSign className="w-3.5 h-3.5" />
            Negociações Abertas
          </div>
          <p className="text-lg font-semibold">{summary.open_deals_count}</p>
          {summary.open_deals_amount > 0 && (
            <p className="text-xs text-muted-foreground">{formatCurrency(summary.open_deals_amount)}</p>
          )}
        </div>

        {/* Weighted */}
        <div className="rounded-lg border bg-muted/30 p-3 space-y-1">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <TrendingUp className="w-3.5 h-3.5" />
            Valor Ponderado
          </div>
          <p className="text-lg font-semibold">{formatCurrency(summary.open_weighted_amount)}</p>
        </div>

        {/* Won amount */}
        {summary.won_deals_amount > 0 && (
          <div className="rounded-lg border bg-muted/30 p-3 space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Trophy className="w-3.5 h-3.5" />
              Ganho Acumulado
            </div>
            <p className="text-lg font-semibold text-primary">{formatCurrency(summary.won_deals_amount)}</p>
          </div>
        )}

        {/* Next close date */}
        {summary.next_expected_close_date && (
          <div className="rounded-lg border bg-muted/30 p-3 space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <CalendarClock className="w-3.5 h-3.5" />
              Próx. Fechamento
            </div>
            <p className="text-sm font-medium">
              {new Date(summary.next_expected_close_date + "T00:00:00").toLocaleDateString("pt-BR")}
            </p>
          </div>
        )}
      </div>

      {/* Primary product */}
      {summary.primary_product_name && (
        <div className="flex items-center gap-2 text-sm">
          <Package className="w-4 h-4 text-muted-foreground" />
          <span className="text-muted-foreground">Produto principal:</span>
          <Badge variant="secondary" className="text-xs">{summary.primary_product_name}</Badge>
        </div>
      )}

      {/* Primary deal info */}
      {summary.primary_open_deal_title && (
        <div className="text-xs text-muted-foreground">
          Deal principal: <span className="font-medium text-foreground">{summary.primary_open_deal_title}</span>
          {summary.primary_open_deal_stage_name && (
            <> · <Badge variant="outline" className="text-[10px] px-1.5 py-0">{summary.primary_open_deal_stage_name}</Badge></>
          )}
        </div>
      )}
    </div>
  );
}
